import { FlexGroupLink } from '@/components'
import { TableSection } from '@/components/Section/TableSection'
import { Badge, Heading, HStack, Icon, Stack, Text } from '@chakra-ui/react'
import { faListTree } from '@fortawesome/pro-duotone-svg-icons'
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ColumnDef } from '@tanstack/react-table'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { organizationListContext, userListContext } from 'BrokerTsLib/src/context'
import type { FlexGroupGridItem, OrganizationUnitGridItem, UserGridItem } from 'BrokerTsLib/src/dtos'
import type { FlexGroupMembershipGridItem } from 'BrokerTsLib/src/dtos/FlexGroup/FlexGroupMembershipGridItem'
import { FlexGroupType } from 'BrokerTsLib/src/enums'
import { useOrganizationFlexGroups } from 'BrokerTsLib/src/hooks/useFlexGroup'
import type { ReactNode } from 'react'
import { UserList, UserListEmailColumn, UserListNameColumn, UserListUserNameColumn } from '../user/UserList'
import { OrganizationListContactPersonColumn, OrganizationListNameColumn, OrganizationsList } from './OrganizationList'

interface SharedOrganizationGridItem extends OrganizationUnitGridItem, SharedByFlexGroupGridItem {
  /** Whether subordinates are shared by the organization. */
  includesSubordinate?: boolean

  /** Whether subordinates are shared to the organization. */
  includingSubordinate?: boolean
}

interface SharedUserGridItem extends UserGridItem, SharedByFlexGroupGridItem {
  includeSubordinate?: boolean
}

interface SharedByFlexGroupGridItem {
  flexGroups: FlexGroupGridItem[]
}

const groupOrganizationsByIdAndSubordinate = (flexGroups: FlexGroupMembershipGridItem[] | undefined): SharedOrganizationGridItem[] => {
  if (!flexGroups) return []

  const organizationMap = new Map<string, SharedOrganizationGridItem>()

  for (const flexGroup of flexGroups) {
    for (const organization of flexGroup.organizationMembers) {
      const key = `${organization.id}-${flexGroup.includeSubordinate ? '1' : '0'}-${organization.includeSubordinate ? '1' : '0'}`
      
      if (organizationMap.has(key)) {
        organizationMap.get(key)!.flexGroups.push(flexGroup)
      } else {
        organizationMap.set(key, {
          ...organization,
          includingSubordinate: flexGroup.includeSubordinate,
          includesSubordinate: organization.includeSubordinate,
          flexGroups: [flexGroup]
        })
      }
    }
  }

  return Array.from(organizationMap.values())
}

const groupUsersByIdAndSubordinate = (flexGroups: FlexGroupMembershipGridItem[] | undefined): SharedUserGridItem[] => {
  if (!flexGroups) return []

  const userMap = new Map<string, SharedUserGridItem>()

  for (const flexGroup of flexGroups) {
    for (const user of flexGroup.userMembers) {
      const key = `${user.userId}-${flexGroup.includeSubordinate ? '1' : '0'}`
        
      if (userMap.has(key)) {
        userMap.get(key)!.flexGroups.push(flexGroup)
      } else {
        userMap.set(key, {
          ...user,
          includeSubordinate: flexGroup.includeSubordinate,
          flexGroups: [flexGroup]
        })
      }
    }
  }

  return Array.from(userMap.values())
}

export const OrganizationUnitSharedFlexGroups = ({
  organizationUnitId,
}: {
  organizationUnitId: number
}) => {

  const { flexGroups, isLoading } = useOrganizationFlexGroups(organizationUnitId, FlexGroupType.Shared)

  const organizations: SharedOrganizationGridItem[] = groupOrganizationsByIdAndSubordinate(flexGroups).sort((a, b) => a.name.localeCompare(b.name))

  const users: SharedUserGridItem[] = groupUsersByIdAndSubordinate(flexGroups).sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName))

  if (organizations.length === 0 && users.length === 0)
    return null

  return (
    <TableSection
      cardHeaderProps={{ py: 2 }}
      sectionTitle="Sdílené skupiny"
      wide
    >
      {organizations.length > 0 && <>
        <Heading px={4} py={3} fontSize='md' fontWeight='semibold' bg='neutral.50' borderBottom='1px solid' borderColor='neutral.100'>Organizace</Heading>
        <organizationListContext.Provider value={{
          loading: isLoading,
          totalCount: organizations.length ?? 0,
          currentPage: 1,
          pageSize: 5,
          filter: { showInvalid: false, showRootOnly: false, pageIndex: 1 },
          setCurrentPage: () => {},
          setPageSize: () => {},
          updateFilter: () => {},
          reload: () => {},
        }}>
          <OrganizationsList organizations={organizations} columnsDefinition={[
            OrganizationListNameColumn,
            OrganizationListContactPersonColumn,
            { 
              key: 'flexGroupName', 
              name: 'Flexibilní skupina', 
              cellExpr: (item) => <SharedByFlexGroupsCell flexGroups={item.flexGroups} />
            },
            {
              key: 'includesSubordinate',
              name: ' ',
              cellExpr: (item) => item.includesSubordinate ? <SharingSubordinatesFromIcon label={<Text><b>{item.name}</b> sdílí přístup ke svým podřízeným organizacím.</Text>} /> : undefined,
              cellProps: { w: 0}
            },
            {
              key: 'includingSubordinate',
              name: ' ',
              cellExpr: (item) => item.includingSubordinate ? <SharingSubordinatesToIcon  label={<Text>Tato organizace sdílí přístup ke svým podřízeným organizacím organizaci <b>{item.name}</b>.</Text>} /> : undefined,
              cellProps: { w: 0}
            },
          ]} />
        </organizationListContext.Provider>
      </>}

      {organizations.length > 0 && users.length > 0 && <Heading px={4} py={3} fontSize='md' fontWeight='semibold' bg='neutral.50' borderBottom='1px solid' borderColor='neutral.100'>Uživatelé</Heading>}

      {users.length > 0 && 
        <userListContext.Provider value={{
          loading: isLoading,
          totalCount: users.length ?? 0,
          currentPage: 1,
          pageSize: 5,
          filter: { showInvalid: false, pageIndex: 1 },
          setCurrentPage: () => {},
          setPageSize: () => {},
          updateFilter: () => {},
          reload: () => {},
        }}>
          <UserList users={users} showFilter={false} showPagination={false} columnsDefinition={[
            UserListNameColumn as ColumnDef<SharedUserGridItem>,
            UserListUserNameColumn as ColumnDef<SharedUserGridItem>,
            UserListEmailColumn as ColumnDef<SharedUserGridItem>,
            { 
              accessorKey: 'flexGroupName', 
              header: 'Flexibilní skupina', 
              cell: (info) => <SharedByFlexGroupsCell flexGroups={info.row.original.flexGroups} />
            },
            {
              accessorKey: 'includeSubordinate',
              header: undefined,
              size: 0,
              cell: (info) => info.getValue() ? <SharingSubordinatesToIcon label={<Text>Tato organizace sdílí přístup ke svým podřízeným organizacím uživateli <b>{info.row.original.firstName} {info.row.original.lastName}</b>.</Text>} /> : undefined
            },
          ]} /> 
        </userListContext.Provider>
      }
    </TableSection>)
}

const SharedByFlexGroupsCell = ({flexGroups}: SharedByFlexGroupGridItem) => {
  if ((flexGroups?.length ?? 0) === 0) return null
  else if (flexGroups.length === 1) {
    const flexGroup = flexGroups[0]
    return <FlexGroupLink name={flexGroup.name} flexGroupId={flexGroup.id} />
  }
  // If there are multiple flex groups, we can show first and then badge with count
  return (
    <HStack>
      <FlexGroupLink name={flexGroups[0].name} flexGroupId={flexGroups[0].id} />
      <CustomTooltip trigger='clickHover' hasArrow label={<Stack p={1}>
        {flexGroups.slice(1).map((group) => (
          <FlexGroupLink key={group.id} name={group.name} flexGroupId={group.id} />
        ))}
      </Stack>}>
        <Badge colorScheme='gray' cursor='pointer'>
          {' + ' + (flexGroups.length - 1)}
        </Badge>
      </CustomTooltip>
    </HStack>
  )

}

const SharingSubordinatesToIcon = ({label}: {label: ReactNode}) => (
  <CustomTooltip boxProps={{ display: 'inline', whiteSpace: 'nowrap'}} label={label} hasArrow>
    <Icon as={FontAwesomeIcon} icon={faListTree} />
    <Icon as={FontAwesomeIcon} icon={faArrowUp} mb={1} boxSize={2.5} color="primary.500" />
  </CustomTooltip>
)

const SharingSubordinatesFromIcon = ({label}: {label: ReactNode}) => (
  <CustomTooltip boxProps={{ display: 'inline', whiteSpace: 'nowrap'}} label={label} hasArrow>
    <Icon as={FontAwesomeIcon} icon={faListTree} />
    <Icon as={FontAwesomeIcon} icon={faArrowDown} mt={1} boxSize={2.5} color="primary.500" />
  </CustomTooltip>
)